import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Compass, Palette, Home } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import FeatureGrid from "@/components/sections/FeatureGrid";
import EditorialServiceBlock from "@/components/sections/EditorialServiceBlock";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/bg-exterior-1.jpg";
import serviceBuy from "@/assets/service-buy.jpg";
import serviceSell from "@/assets/service-sell.jpg";
import serviceInvest from "@/assets/service-invest.jpg";
import serviceDesign from "@/assets/service-design.jpg";
import servicePrep from "@/assets/service-prep.jpg";

const WorkWithUs = () => {
  useEffect(() => {
    document.title = "Work With Hadassah Homes | Buy, Sell & Invest in Massachusetts Real Estate";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Work with Hadassah Homes to buy, sell, or invest in real estate with confidence. We combine real estate expertise, interior design, and property preparation services to help you achieve the best results.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://hadassahhomesinc.com/work-with-us");
    return () => {
      document.title = "Massachusetts Real Estate & Dominican Republic Investment | Hadassah Homes";
      if (meta) meta.setAttribute("content", "Buy, sell, and invest with confidence. Hadassah Homes offers real estate, interior design, and property preparation services in Massachusetts and the Dominican Republic.");
      if (canonical) canonical.setAttribute("href", "https://hadassahhomesinc.com");
    };
  }, []);

  return (
    <SiteLayout>
      {/* HERO */}
      <PageHero
        eyebrow="Work With Hadassah Homes"
        title="Work With Hadassah Homes"
        backgroundImage={heroImage}
      />

      {/* INTRO — Real Estate Done Differently */}
      <ContentSection>
        <div className="max-w-3xl mx-auto">
          <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight mb-8">
            Real Estate Done Differently
          </h2>
          <div className="space-y-6 text-muted-foreground text-base md:text-lg leading-relaxed">
            <p>Most real estate experiences are transactional. You're handed listings, given basic guidance, and left to figure out the rest.</p>
            <p>At Hadassah Homes, we take a different approach.</p>
            <p>We combine real estate strategy, interior design insight, and hands-on property preparation to help you make smarter decisions and achieve stronger results, whether you're buying, selling, or investing.</p>
            <p>This isn't just about completing a transaction. It's about positioning you to win.</p>
          </div>
        </div>
      </ContentSection>

      {/* SERVICE BLOCKS */}
      <ContentSection variant="dark">
        <SectionHeader
          eyebrow="Our Services"
          title="Choose the Path That Fits Your Goals"
          description="No two clients are the same. That's why we've built services designed to meet you where you are, and move you forward with a clear, strategic plan."
        />

        <EditorialServiceBlock
          title="Buy a Home"
          intro="Buy with clarity, confidence, and a competitive edge."
          description={[
            "We guide you through every step of the process, from understanding the market and securing financing to identifying the right property and making a strong offer.",
          ]}
          checklists={[
            {
              label: "What you can expect:",
              items: [
                "A clear, step-by-step buying strategy",
                "Insight into Massachusetts markets and opportunities",
                "Support tailored for both first-time and experienced buyers",
                "Guidance to help you compete and win in competitive markets",
              ],
            },
          ]}
          nudge="Start your home search the right way"
          cta={{ text: "Learn More About Buying a Home", href: "/buy-a-home" }}
          image={serviceBuy}
          imageAlt="Modern home interior with natural light and hardwood floors"
        />

        <EditorialServiceBlock
          title="Sell Your Home"
          intro="Sell smarter, and maximize what your home is truly worth."
          description={[
            "Most agents list homes. We prepare and position them to perform.",
            "Through a combination of strategic pricing, marketing, and hands-on property preparation, we help your home stand out, attract stronger buyers, and sell with confidence.",
          ]}
          checklists={[
            {
              label: "What sets us apart:",
              items: [
                "Strategic pricing and market positioning",
                "Professional cleaning to elevate first impressions",
                "Carpentry and repairs to eliminate buyer objections",
                "Interior and exterior painting to modernize and enhance appeal",
                "High-quality presentation and marketing",
              ],
            },
          ]}
          nudge="Position your home to sell at its best"
          cta={{ text: "Learn More About Selling Your Home", href: "/sell-your-home" }}
          image={serviceSell}
          imageAlt="Beautifully staged home with excellent curb appeal"
          reverse
        />

        <EditorialServiceBlock
          title="Invest in Real Estate"
          intro="Make smarter investment decisions with a long-term strategy."
          description={[
            "We help you identify opportunities, evaluate risk, and build a plan that aligns with your financial goals, whether you're just getting started or expanding your portfolio.",
          ]}
          checklists={[
            {
              label: "What you can expect:",
              items: [
                "Guidance on rental, flip, and long-term investment strategies",
                "Market insight in Massachusetts and the Dominican Republic",
                "Support in identifying high-potential opportunities",
                "A focus on sustainable growth and returns",
              ],
            },
          ]}
          nudge="Explore your next investment move"
          cta={{ text: "Learn More About Investing", href: "/invest" }}
          image={serviceInvest}
          imageAlt="Modern apartment building at golden hour"
        />

        <EditorialServiceBlock
          title="Interior Design"
          intro="Design with intention, whether for living or selling."
          description={[
            "Our design approach goes beyond aesthetics. We focus on creating spaces that enhance lifestyle, increase value, and attract buyers when it matters most.",
          ]}
          checklists={[
            {
              label: "What you can expect:",
              items: [
                "Design strategies that improve both function and presentation",
                "Guidance on finishes, layout, and visual appeal",
                "Support for staging and preparing homes for market",
                "A cohesive look that elevates the entire space",
              ],
            },
          ]}
          nudge="Enhance your space with purpose"
          cta={{ text: "Learn More About Interior Design", href: "/interior-design" }}
          image={serviceDesign}
          imageAlt="Elegantly styled modern interior with designer furniture"
          reverse
        />

        <EditorialServiceBlock
          title="Property Preparation Services"
          intro="Your unfair advantage in the market."
          description={[
            "This is where Hadassah Homes stands apart.",
            "We offer hands-on services that most agents don't, giving you the ability to prepare, improve, and elevate your property before it ever hits the market.",
          ]}
          checklists={[
            {
              label: "Services include:",
              items: [
                "Professional Cleaning. Make your home show-ready and inviting",
                "Carpentry & Repairs. Fix issues that could impact value or inspections",
                "Interior & Exterior Painting. Refresh and modernize your space",
              ],
            },
            {
              label: "These aren't just add-ons. They're strategic tools designed to help you:",
              items: [
                "Increase your home's value",
                "Attract stronger buyers",
                "Reduce time on market",
              ],
            },
          ]}
          nudge="Get your property market-ready"
          cta={{ text: "Learn More About Property Preparation", href: "/property-preparation" }}
          image={servicePrep}
          imageAlt="Professional property renovation in progress"
        />
      </ContentSection>

      {/* A Smarter, Full-Service Approach */}
      <ContentSection>
        <SectionHeader title="A Smarter, Full-Service Approach" />
        <div className="max-w-3xl mx-auto -mt-8">
          <div className="space-y-6 text-muted-foreground text-base md:text-lg leading-relaxed">
            <p>When everything works together, real estate, design, and preparation, you get better results.</p>
            <p>Instead of managing multiple vendors and guessing what matters most, you get a clear, aligned strategy from start to finish.</p>
            <p>Every decision is intentional. Every step is focused on your outcome.</p>
          </div>
        </div>
        <div className="mt-16">
          <FeatureGrid
            columns={3}
            items={[
              { icon: Compass, title: "Real Estate Strategy", description: "Guiding you through buying, selling, or investing with clarity, data, and confidence." },
              { icon: Palette, title: "Interior Design Insight", description: "Positioning properties to stand out through thoughtful design and presentation." },
              { icon: Home, title: "Property Preparation", description: "Hands-on property preparation services that prepare your property to attract attention and perform." },
            ]}
          />
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={serviceDesign}
          alt="Hadassah Homes consultation and design planning"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/80 via-black/65 to-black/90" />

        <div className="relative z-10 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-[42rem] text-center">
            <p className="mb-4 font-body text-xs uppercase tracking-[0.14em] text-accent">
              Next Step
            </p>
            <h2 className="mb-5 font-headline text-3xl leading-[1.08] text-white sm:text-4xl md:text-[3.45rem]">
              Not Sure Where to Start?
            </h2>
            <p className="mx-auto mb-12 max-w-2xl text-base leading-[1.8] text-white/90 md:text-lg">
              You don&apos;t have to figure it out alone. Whether you&apos;re buying, selling, investing, or preparing a property, we&apos;ll help you identify the right path and create a plan that works for you. Book a consultation today and take the next step with confidence.
            </p>
            <Button
              variant="gold"
              size="lg"
              asChild
              className="bg-[#C9A95C] text-black hover:bg-[#b8954f]"
            >
              <Link to="/consultation">Book a Consultation</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default WorkWithUs;
